import Icon1 from "@assets/icons/Icon";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CheckBoxInput from "../selection/CheckBoxInput";

export type DropDownItem = {
  value: string | number;
  label: string | React.ReactNode;
  extra_label?: string;
  type?: string;
};

type DropDownMultipleProps = {
  disabled?: boolean;
  label: string;
  options: DropDownItem[];
  value?: (string | number)[]; // Array of selected values
  onSelect: (items: (string | number)[]) => void;
  className?: string;
  mandatory?: boolean;
  grow_on_focus?: boolean;
  category?: string;
  placeholder?: string;
};

export default function DropDownMultiple({
  disabled = false,
  label,
  options,
  category,
  value = [],
  onSelect,
  className,
  mandatory = false,
  grow_on_focus = false,
  placeholder,
}: DropDownMultipleProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [openUpwards, setOpenUpwards] = useState<boolean | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLUListElement>(null);

  const handleToggle = (itemValue: string | number) => {
    const newValue = value.includes(itemValue)
      ? value.filter((v) => v !== itemValue)
      : [...value, itemValue];

    onSelect(newValue);
  };

  const handleSelectAll = () => {
    const allValues = options.map((item) => item.value);
    onSelect(allValues);
  };

  const handleClearAll = () => {
    onSelect([]);
  };

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldOpenUpwards = spaceAbove > spaceBelow && spaceBelow < 200;
      setOpenUpwards(shouldOpenUpwards);

      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 8 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      if (portalRef.current && portalRef.current.contains(target)) {
        return;
      }

      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const has_value = value.length > 0;
  const disabled_classname =
    "focus:outline-none focus:ring-transparent focus:border-neutral-300 focus:border-1 text-shadow_25 bg-neutral-100 border-neutral-300";

  // Get selected labels for display
  const getSelectedLabels = () => {
    if (value.length === 0) return placeholder || label;
    if (value.length === options.length) return "Tous sélectionnés";
    if (value.length === 1) {
      return options.find((opt) => opt.value === value[0])?.label || "";
    }
    if (value.length > 1 && value.length < 4) {
      return value
        .map((val) => options.find((opt) => opt.value === val)?.label || "")
        .join(", ");
    }
    return `${value.length} sélectionné${value.length > 1 ? "s" : ""}`;
  };

  // Portal dropdown content
  const dropdownContent = isOpen ? (
    <ul
      ref={portalRef}
      style={{
        position: "fixed",
        top: openUpwards
          ? dropdownPosition.top - 95
          : dropdownPosition.top - 70,
        left: dropdownPosition.left,
        maxWidth: dropdownPosition.width,
        zIndex: 10001, // above BaseModal (z-[10000])
      }}
      className="flex flex-col w-full min-w-200 bg-white shadow-lg rounded-xl border sm:text-sm"
    >
      {/* Header with select all/clear all */}
      <div className="sticky top-0 border-b pb-4 p-2">
        <div className="text-neutral-400 text-h3 px-2 py-2">
          {category || label}
        </div>
        <div className="flex flex-row gap-2 px-2">
          <CheckBoxInput
            label="Tout sélectionner"
            checked={value.length === options.length}
            onChange={(n) =>
              value.length !== options.length
                ? handleSelectAll()
                : handleClearAll()
            }
          />
        </div>
      </div>
      <div className="p-2 overflow-y-auto max-h-200">
        {/* Options */}
        {options
          .filter((item) => item?.value !== undefined)
          .map((item) => {
            const isChecked = value.includes(item.value);

            return (
              <li
                key={item.value}
                className={`cursor-pointer select-none relative rounded-lg py-2 pl-2 pr-2 hover:bg-neutral-50`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggle(item.value);
                }}
              >
                <div className="flex items-center gap-2">
                  <CheckBoxInput
                    checked={isChecked}
                    onChange={() => handleToggle(item.value)}
                  />
                  <span className="block text-h2  flex-1">
                    {item.label}
                    {item.extra_label && (
                      <span
                        className={`${
                          item.type
                            ? item.type === "particulier"
                              ? "text-purple-400 bg-purple-50"
                              : item.type === "constructor"
                                ? "text-neutral-400 bg-neutral-50"
                                : item.type === "expert"
                                  ? "text-green-400 bg-green-50"
                                  : "text-blue-400 bg-blue-50"
                            : "text-blue-400 bg-blue-50"
                        } font-light rounded-full px-1 text-h3 ml-1`}
                      >
                        {item.extra_label}
                      </span>
                    )}
                  </span>
                </div>
              </li>
            );
          })}

        {(options || []).length === 0 && (
          <li className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
            Aucune option disponible
          </li>
        )}
      </div>
    </ul>
  ) : null;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="text-h4 text-neutral-400 ">{label}</div>{" "}
      <div
        className={`relative select-none outline-none border-0 ${className}`}
        style={{ width: grow_on_focus ? (isOpen ? "" : "150px") : "" }}
      >
        <div
          ref={dropdownRef}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          tabIndex={0}
          className={`relative w-full px-3 outline-none h-fit  bg-white border flex py-3 items-center flex-row justify-between rounded-xl shadow-sm ${
            disabled
              ? disabled_classname
              : mandatory && !has_value && !isOpen
                ? "border-red-300 cursor-pointer border-2 outline-1 outline-red-100"
                : "cursor-pointer bg-transparent border"
          } sm:text-sm`}
        >
          {mandatory && !has_value && !isOpen && (
            <div className="absolute top-0 justify-start h-full items-start flex py-0 flex-row right-8 text-white text-h6">
              <div className="bg-red-300 flex flex-row items-end rounded-b-lg px-1 h-5 pb-0.5">
                Champ obligatoire
              </div>
            </div>
          )}

          {!isOpen && (
            <div className="absolute top-1/3 right-2 px-1">
              <Icon1
                name="down"
                size={15}
                color={mandatory && !has_value && !isOpen ? "red" : "grey"}
              />
            </div>
          )}
          {isOpen && (
            <div className="absolute top-1/3 right-3">
              <Icon1 name="up" size={15} color="grey" />
            </div>
          )}

          <div className="flex flex-row items-center justify-between w-full mr-4 text-wrap overflow-hidden">
            <div className="flex flex-row justify-between items-center w-full">
              <span
                className={`${!has_value ? "text-neutral-400" : "text-black"}`}
              >
                {getSelectedLabels()}
              </span>
            </div>
          </div>
        </div>

        {typeof window !== "undefined" &&
          createPortal(dropdownContent, document.body)}
      </div>
    </div>
  );
}
